import React from 'react';
import { Check } from 'lucide-react';
import { useLocalization } from '../context/LocalizationContext';

export const LanguageSwitcher = ({ compact = false, className = '' }) => {
  const { language, setLanguage, supportedLanguages, t } = useLocalization();
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const currentLang = supportedLanguages.find((lang) => lang.code === language);

  return (
    <div
      ref={containerRef}
      className={`relative inline-flex ${className}`}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`inline-flex items-center justify-center gap-2 rounded-xl border border-primary-100 dark:border-slate-700 bg-white dark:bg-slate-900 text-primary-800 dark:text-primary-200 hover:bg-primary-50 dark:hover:bg-slate-800 transition-all ${
          compact ? 'h-10 px-3' : 'h-11 px-4'
        }`}
        aria-label={t('common.language')}
        title={t('common.language')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-testid="language-switcher-button"
      >
        <span className="font-semibold text-sm">
            {currentLang?.code === 'en' ? 'Aa' : currentLang?.code === 'te' ? 'అ' : currentLang?.code?.toUpperCase() || 'Aa'}
          </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="fixed left-3 right-3 top-[4.35rem] z-[70] overflow-hidden rounded-xl border border-primary-100 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg sm:absolute sm:left-auto sm:right-0 sm:top-full sm:z-50 sm:mt-2 sm:min-w-[160px]"
          role="menu"
          aria-label={t('common.language')}
        >
          <div className="p-1">
            {supportedLanguages.map((item) => {
              const isActive = language === item.code;
              return (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => {
                    setLanguage(item.code);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm text-left transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                      : 'text-primary-800 dark:text-primary-200 hover:bg-primary-50 dark:hover:bg-slate-800'
                  }`}
                  role="menuitemradio"
                  aria-checked={isActive}
                  data-testid={`language-switch-${item.code}`}
                >
                  <span className="font-medium">{item.nativeLabel}</span>
                  {isActive && <Check size={14} className="shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
